import React, { useCallback, useState, useEffect, useRef } from "react"
import { Badge } from "antd"
import { ExportMapCategory } from "src/types/exportMap.types"
import { useExportMapStore } from "stores/exportMapStore"
import { hasCategoryOriginalCode } from "./utils/exportMapMerger"

interface ExportMapCategoryRowProps {
  variableLabel: string
  category: ExportMapCategory
}

/**
 * Row component for editing a single category's code.
 * Displays category label and code input with original code indicator.
 * Memoized to prevent re-renders when other categories change.
 */
const ExportMapCategoryRow: React.FC<ExportMapCategoryRowProps> = React.memo(({
  variableLabel,
  category,
}) => {
  // Use selector to only subscribe to the specific action needed
  const updateCategoryCode = useExportMapStore((state) => state.updateCategoryCode)
  const [localCode, setLocalCode] = useState(String(category.code))
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Sync local state when category changes from external source (e.g., reset, import)
  useEffect(() => {
    setLocalCode(String(category.code))
  }, [category.code])

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const isOriginal = hasCategoryOriginalCode(category)

  /**
   * Handle code change - update local state immediately, debounce store update
   */
  const handleCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newCode = e.target.value
      setLocalCode(newCode)

      // Debounce store update for performance (longer delay for smoother typing)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        const code = /^\d+$/.test(newCode) ? parseInt(newCode, 10) : newCode
        updateCategoryCode(variableLabel, category.label, code)
      }, 400)
    },
    [variableLabel, category.label, updateCategoryCode]
  )

  /**
   * Sync to store immediately on blur
   */
  const handleCodeBlur = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    if (localCode !== String(category.code)) {
      const code = /^\d+$/.test(localCode) ? parseInt(localCode, 10) : localCode
      updateCategoryCode(variableLabel, category.label, code)
    }
  }, [localCode, category.code, category.label, variableLabel, updateCategoryCode])

  return (
    <div className="flex items-center gap-2 py-1 border-b border-gray-100 last:border-b-0">
      {/* Category Label */}
      <span className="flex-1 text-sm text-gray-700 truncate">
        {category.label}
      </span>

      {/* Dashed line connector */}
      <span className="flex-1 border-b border-dashed border-gray-300" />

      {/* Code Input */}
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={localCode}
          onChange={handleCodeChange}
          onBlur={handleCodeBlur}
          className="w-20 px-2 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
        />

        {/* Original/Auto indicator */}
        <Badge
          count={isOriginal ? "ORIG" : "AUTO"}
          style={{
            backgroundColor: isOriginal ? "#52c41a" : "#faad14",
            fontSize: 8,
            padding: "0 3px",
            lineHeight: "14px",
            height: "14px",
          }}
        />
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if the category actually changed
  return (
    prevProps.variableLabel === nextProps.variableLabel &&
    prevProps.category.label === nextProps.category.label &&
    prevProps.category.code === nextProps.category.code
  )
})

export default ExportMapCategoryRow
